import aiAgent from './ai-agent';
// import aiInterviewTranscriptService from "./aiInterviewTranscriptService"


class AIAgentService {
    constructor() {
        this.isActive = false;
        this._ownsMicStream = false;

        // Audio playback (Deepgram → speaker)
        this.audioContext = null;
        this.nextPlayTime = 0;

        // Mic capture (mic → Deepgram)
        this.micStream = null;
        this.micContext = null;
        this.micProcessor = null;

        // Optional external callbacks
        this.onTranscript = null;       // (role, text) => void
        this.onQuestionComplete = null; // (questionNumber, result) => void
        this.onOpenCodeEditor = null;   // () => void
        this.onSpeakingChange = null;   // (isSpeaking: boolean) => void
        this.onReady = null;            // () => void
        this.onAudioChunk = null
        this.onCurrentQuestion = null;   // (id: string) => void
        this.onInterviewEnd = null;      // () => void
    }

    // ── START ──────────────────────────────────────────────────────────────────
    async start(candidateName = 'Candidate', existingMicStream = null, questions = [], roomId = 'unknown', companyName = '', additionalInstructions = '') {
        if (this.isActive) {
            console.warn('⚠️ AI agent already running');
            return;
        }

        console.log('🚀 Starting AI agent...');
        this._questions = Array.isArray(questions) ? questions : [];
        this._currentQuestionIndex = 0;

        // await aiInterviewTranscriptService.startSession(roomId, candidateName)

        try {
            this.isActive = true;
            aiAgent.candidateName = candidateName;
            aiAgent.companyName = companyName;
            aiAgent.additionalInstructions = additionalInstructions; 

            aiAgent.questions = this._questions;

            // 1. Set up AudioContext for 24kHz playback
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 24000,
            });
            this.nextPlayTime = 0;

            // 2. Wire agent callbacks
            aiAgent.onAudioChunk = (pcmBuffer) => {
                this._playPCMChunk(pcmBuffer)
                // Tap: send same PCM to recording server in parallel
                if (typeof this.onAudioChunk === 'function') {
                    this.onAudioChunk(pcmBuffer)
                }
            }

            aiAgent.onTranscript = (role, text, timestamps) => {
                console.log(`[${role}]: ${text}`)
                const mappedRole = role === 'user' ? 'candidate' : 'interviewer'
                if (typeof this.onTranscript === 'function') this.onTranscript(mappedRole, text, timestamps)
            }

            aiAgent.onQuestionComplete = (num, result) => {
                console.log(`📋 Q${num} — ${result}`);
                if (typeof this.onQuestionComplete === 'function') {
                    this.onQuestionComplete(num, result);
                }
            };

            aiAgent.onOpenCodeEditor = () => {
                console.log('📂 Open code editor triggered');
                if (typeof this.onOpenCodeEditor === 'function') this.onOpenCodeEditor();
            };

            aiAgent.onCurrentQuestion = (id) => {
                console.log('📍 [Orchestrator] Current question id:', id);
                if (typeof this.onCurrentQuestion === 'function') this.onCurrentQuestion(id);
            };

            aiAgent.onInterviewEnd = () => {
                console.log('🏁 [Orchestrator] Interview end received');
                if (typeof this.onInterviewEnd === 'function') this.onInterviewEnd();
            };

            aiAgent.onSpeakingChange = (speaking) => {
                if (typeof this.onSpeakingChange === 'function') this.onSpeakingChange(speaking);
            };
            aiAgent.onReady = () => {
                console.log('✅ AI agent ready');
                if (typeof this.onReady === 'function') this.onReady();
            };

            // 3. Connect to Deepgram
            await aiAgent.initialize();

            // 4. Start mic → Deepgram stream
            await this._startMicStream(existingMicStream);

            console.log('✅ AI agent fully started');
        } catch (error) {
            console.error('❌ Failed to start AI agent:', error);
            this.isActive = false;
            await this._cleanup();
            throw error;
        }
    }

    injectMessage(text) {
        aiAgent.injectMessage(text);
    }

    // ── STOP ───────────────────────────────────────────────────────────────────

    async stop() {
        if (!this.isActive) return;
        console.log('🛑 Stopping AI agent...');
        this.isActive = false;
        // await aiInterviewTranscriptService.endSession()
        await this._cleanup();
        console.log('✅ AI agent stopped');
    }

    // ── MIC → DEEPGRAM ─────────────────────────────────────────────────────────

    async _startMicStream(existingStream = null) {
        // FIX 1: Reuse an already-open mic stream instead of calling getUserMedia
        // again. The page already holds a stream for the video UI — opening a
        // second capture means Deepgram receives two identical audio feeds and
        // processes every utterance twice, causing duplicate agent responses.
        if (existingStream && existingStream.getAudioTracks().length > 0) {
            this.micStream = existingStream;
            this._ownsMicStream = false; // don't stop tracks we didn't open
            console.log('🎤 Reusing existing mic stream');
        } else {
            this.micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 16000,
                },
            });
            this._ownsMicStream = true;
            console.log('🎤 Opened new mic stream');
        }

        this.micContext = new AudioContext({ sampleRate: 16000 });
        const source = this.micContext.createMediaStreamSource(this.micStream);
        this.micProcessor = this.micContext.createScriptProcessor(4096, 1, 1);

        this.micProcessor.onaudioprocess = (e) => {
            if (!this.isActive) return;
            const float32 = e.inputBuffer.getChannelData(0);
            const int16 = new Int16Array(float32.length);
            for (let i = 0;i < float32.length;i++) {
                int16[i] = Math.max(-1, Math.min(1, float32[i])) * 0x7fff;
            }
            aiAgent.sendAudioChunk(int16.buffer);
        };

        source.connect(this.micProcessor);
        this.micProcessor.connect(this.micContext.destination);
        console.log('🎤 Mic streaming started');
    }

    // ── PCM PLAYBACK ───────────────────────────────────────────────────────────

    _playPCMChunk(arrayBuffer) {
        if (!this.audioContext) return;

        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        const int16 = new Int16Array(arrayBuffer);
        const float32 = new Float32Array(int16.length);
        for (let i = 0;i < int16.length;i++) {
            float32[i] = int16[i] / 32768.0;
        }

        const SOURCE_RATE = 24000;
        const ctxRate = this.audioContext.sampleRate;
        let final = float32;

        if (ctxRate !== SOURCE_RATE) {
            const ratio = ctxRate / SOURCE_RATE;
            const resampled = new Float32Array(Math.round(float32.length * ratio));
            for (let i = 0;i < resampled.length;i++) {
                const idx = i / ratio;
                const lo = Math.floor(idx);
                const hi = Math.min(lo + 1, float32.length - 1);
                const frac = idx - lo;
                resampled[i] = float32[lo] * (1 - frac) + float32[hi] * frac;
            }
            final = resampled;
        }

        const buffer = this.audioContext.createBuffer(1, final.length, ctxRate);
        buffer.copyToChannel(final, 0);

        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);

        const startTime = Math.max(this.audioContext.currentTime, this.nextPlayTime);
        source.start(startTime);
        this.nextPlayTime = startTime + buffer.duration;
    }

    // ── CLEANUP ────────────────────────────────────────────────────────────────

    async _cleanup() {
        aiAgent.reset();

        // aiInterviewTranscriptService.reset()

        if (this.micProcessor) {
            this.micProcessor.disconnect();
            this.micProcessor = null;
        }
        if (this.micStream) {
            // Only stop tracks if this service opened the stream itself.
            // If we reused the page's stream, the page is responsible for stopping it.
            if (this._ownsMicStream) {
                this.micStream.getTracks().forEach((t) => t.stop());
            }
            this.micStream = null;
            this._ownsMicStream = false;
        }
        if (this.micContext) {
            try { await this.micContext.close(); } catch (_) { }
            this.micContext = null;
        }
        if (this.audioContext) {
            try { await this.audioContext.close(); } catch (_) { }
            this.audioContext = null;
        }
        this.nextPlayTime = 0;
    }
}

// Singleton
const aiAgentService = new AIAgentService();
export default aiAgentService;